import { useState, useCallback } from 'react'
import { api } from '../lib/api'

interface Suggestion {
  text: string
  reason: string
}

interface UseChatSuggestionsReturn {
  suggestions: Suggestion[]
  loadingSuggestions: boolean
  showSuggestions: boolean
  coachTip: string
  suggestionError: string
  setShowSuggestions: (show: boolean) => void
  handleGetSuggestions: () => Promise<void>
  handleSelectSuggestion: (text: string) => void
}

export function useChatSuggestions(
  matchId: string | undefined,
  setInput: (value: string) => void
): UseChatSuggestionsReturn {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [coachTip, setCoachTip] = useState('')
  const [suggestionError, setSuggestionError] = useState('')

  const handleGetSuggestions = useCallback(async () => {
    if (loadingSuggestions || !matchId) return
    setLoadingSuggestions(true)
    setShowSuggestions(true)
    setSuggestionError('')
    try {
      const data = await api.matches.suggest(parseInt(matchId))

      // 포인트 부족 등의 에러 체크
      if (data.error) {
        setSuggestionError(data.error)
        setSuggestions([])
        setCoachTip('')
        return
      }

      // Handle both old format (string[]) and new format ({ text, reason }[])
      const rawSuggestions = data.suggestions || []
      const formattedSuggestions: Suggestion[] = rawSuggestions.map((s: string | Suggestion) => {
        if (typeof s === 'string') {
          return { text: s, reason: '' }
        }
        return s
      })
      setSuggestions(formattedSuggestions)
      setCoachTip(data.coachTip || '')
    } catch {
      setSuggestions([])
      setCoachTip('')
      setSuggestionError('제안을 불러오는데 실패했습니다.')
    } finally {
      setLoadingSuggestions(false)
    }
  }, [loadingSuggestions, matchId])

  const handleSelectSuggestion = useCallback((text: string) => {
    setInput(text)
    setShowSuggestions(false)
  }, [setInput])

  return {
    suggestions,
    loadingSuggestions,
    showSuggestions,
    coachTip,
    suggestionError,
    setShowSuggestions,
    handleGetSuggestions,
    handleSelectSuggestion,
  }
}
